import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { PostmanIcon } from "./PostmanIcon";

/**
 * Lightweight, modal-style overlay showing the focused 3-section
 * Disclaimer & Terms text the user must consent to at checkout.
 * Pulls content from i18n key `disclaimer` so it stays in sync with
 * the consent checkbox copy.
 */
export const DisclaimerModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const isZH = (i18n.language || "en").startsWith("zh");
  const sections = t("disclaimer.sections", { returnObjects: true }) || [];

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="disclaimer-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-[#1C1A17]/72 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[88vh] bg-[var(--bg-paper)] border border-[var(--rule)] shadow-[0_30px_80px_-20px_rgba(28,26,23,0.6)] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-7 md:px-10 pt-9 pb-5 border-b border-[var(--rule)] flex items-start justify-between gap-4">
          <div>
            <PostmanIcon size={36} className="mb-3" />
            <p className="text-[10px] uppercase tracking-[0.42em] text-[var(--wax)] mb-2">
              {isZH ? "— 法律文件 —" : "— A legal document —"}
            </p>
            <h2
              className={`${isZH ? "font-cn" : "font-display"} text-2xl md:text-3xl tracking-tight text-[var(--ink)]`}
              data-testid="disclaimer-modal-title"
            >
              {t("disclaimer.title")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="disclaimer-modal-close"
            className="text-[var(--ink-soft)] hover:text-[var(--ink)] -mt-1 -mr-1 p-2"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-7 md:px-10 py-7 space-y-8 text-[var(--ink)]">
          {Array.isArray(sections) && sections.map((sec, i) => (
            <section key={i} data-testid={`disclaimer-section-${i}`}>
              <h3 className={`${isZH ? "font-cn" : "font-display"} text-[18px] md:text-[19px] font-medium mb-3 text-[var(--ink)]`}>
                {sec.heading}
              </h3>
              <p className={`${isZH ? "font-cn" : "font-en"} text-[15px] md:text-[16px] leading-[1.95] text-[var(--ink)]`}>
                {sec.body}
              </p>
            </section>
          ))}
          <div className="pt-2 border-t border-dashed border-[var(--rule)]">
            <p className="text-[12px] italic text-[var(--ink-mute)] text-center">
              {t("disclaimer.footer_note")}
            </p>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="px-7 md:px-10 py-5 border-t border-[var(--rule)] bg-[var(--bg-paper-deep)]/40 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            data-testid="disclaimer-modal-acknowledge"
            className="px-6 py-2.5 text-[12px] uppercase tracking-[0.22em] border border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg-paper)] transition-colors"
          >
            {t("disclaimer.acknowledge_btn")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerModal;
